import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Printer as PrinterIcon, RefreshCw, Search, Monitor, CheckCircle2, XCircle, AlertTriangle, Star } from 'lucide-react';
import { BackButton } from '../../../common/BackButton';
import { useAuth } from '../../../../contexts/AuthContext';
import { useTranslation } from '../../../../contexts/I18nProvider';
import printNodeService from '../../../../services/printNodeService';
import type { Printer, Computer, WhoAmI, PrintJob } from '../../../../types/printNode';
import type { ParsedCapabilities, ParsedPaperFormat } from './types';
import { PrinterAccessPanel } from './PrinterAccessPanel';

interface PrintNodePrintersProps {
  onBack?: () => void;
}

/** Antall siste jobber vi ber PrintNode om, delt på tvers av alle skrivere. */
const RECENT_JOBS_LIMIT = 100;
/** Hvor mange av de siste jobbene som vises per skriver-kort. */
const RECENT_JOBS_PER_PRINTER = 3;

/**
 * Rent lesevisning av alle PrintNode-skrivere for tenanten, med kapabiliteter
 * (DPI, papirformater, bins, farge/duplex-støtte) hentet direkte fra PrintNode —
 * pluss kontostatus og siste jobber per skriver.
 *
 * Formål: rask oppslag ved feilsøking av rapport-/skriverkonfigurasjon — man skal
 * slippe å spørre PrintNode-API manuelt for å se hvilke papirformater en skriver har
 * (jf. papirmatch-saken hos Flemming Egg/Norlux, 2026-09-21: WMS sender nå `paper`
 * eksplisitt ut fra rapportens Bredde×Lengde matchet mot nettopp denne listen), eller
 * hvilken PrintNode-klientversjon en PC kjører (relevant etter Engine6-funnene samme sak).
 *
 * Ingen redigering, ingen kobling til rapporter/pakksoner/profiler i denne omgangen.
 */
export const PrintNodePrinters: React.FC<PrintNodePrintersProps> = ({ onBack }) => {
  const { user } = useAuth();
  const { t } = useTranslation();
  const companyNo = user?.companyNo || '';

  const [printers, setPrinters] = useState<Printer[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [lastFetchedAt, setLastFetchedAt] = useState<Date | null>(null);
  const [search, setSearch] = useState('');

  // Kontoinfo og siste jobber er begge "nice to have" — feiler ett av kallene
  // skal ikke resten av siden bli utilgjengelig, derfor egne loading/error-tilstander.
  const [account, setAccount] = useState<WhoAmI | null>(null);
  const [accountError, setAccountError] = useState(false);
  const [recentJobs, setRecentJobs] = useState<PrintJob[]>([]);
  // TW 36430601: fane for skriverfilteret (tenant, lager, profil)
  const [tab, setTab] = useState<'printers' | 'access'>('printers');

  const load = useCallback(async () => {
    if (!companyNo) return;
    setLoading(true);
    setError(null);
    try {
      const data = await printNodeService.getPrinters(companyNo, 'tenant');
      setPrinters(data);
      setLastFetchedAt(new Date());
    } catch (err) {
      console.error('Failed to load PrintNode printers:', err);
      setError(
        t('manager.modules.printNodePrinters.errors.loadFailed', {}, 'Kunne ikke hente skrivere fra PrintNode. Sjekk at PrintNode.ApiKey er satt riktig.')
      );
    } finally {
      setLoading(false);
    }

    // Kontoinfo og jobbhistorikk er diagnostiske ekstrafelt — logg og fortsett ved feil.
    setAccountError(false);
    try {
      const who = await printNodeService.verifyConnection(companyNo);
      setAccount(who);
    } catch (err) {
      console.warn('Failed to load PrintNode account info:', err);
      setAccount(null);
      setAccountError(true);
    }

    try {
      const jobs = await printNodeService.getRecentPrintJobs(companyNo, RECENT_JOBS_LIMIT);
      setRecentJobs(jobs);
    } catch (err) {
      console.warn('Failed to load recent PrintNode jobs:', err);
      setRecentJobs([]);
    }
  }, [companyNo, t]);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return printers;
    return printers.filter(p =>
      p.name.toLowerCase().includes(q) ||
      p.description?.toLowerCase().includes(q) ||
      String(p.id).includes(q) ||
      p.computer?.hostname?.toLowerCase().includes(q) ||
      p.computer?.name?.toLowerCase().includes(q)
    );
  }, [printers, search]);

  // Grupper etter computer — flere skrivere deler ofte samme PC/PrintNode-klient,
  // og "hvilken PC må jeg restarte tjenesten på" er nettopp typen spørsmål denne siden svarer på.
  const groups = useMemo(() => {
    const map = new Map<number, { computer: Computer | undefined; printers: Printer[] }>();
    for (const p of filtered) {
      const key = p.computer?.id ?? p.computerId ?? -1;
      const existing = map.get(key);
      if (existing) {
        existing.printers.push(p);
      } else {
        map.set(key, { computer: p.computer, printers: [p] });
      }
    }
    return Array.from(map.values()).sort((a, b) =>
      (a.computer?.hostname || a.computer?.name || '').localeCompare(b.computer?.hostname || b.computer?.name || '')
    );
  }, [filtered]);

  const jobsByPrinterId = useMemo(() => {
    const map = new Map<number, PrintJob[]>();
    const sorted = [...recentJobs].sort(
      (a, b) => new Date(b.createTimestamp).getTime() - new Date(a.createTimestamp).getTime()
    );
    for (const job of sorted) {
      const list = map.get(job.printerId);
      if (list) {
        if (list.length < RECENT_JOBS_PER_PRINTER) list.push(job);
      } else {
        map.set(job.printerId, [job]);
      }
    }
    return map;
  }, [recentJobs]);

  return (
    <div className="flex flex-col h-full bg-theme-bg-primary">
      <div className="flex items-center justify-between p-4 border-b border-theme-border-primary gap-3 flex-wrap">
        <div className="flex items-center gap-3">
          {onBack && <BackButton onClick={onBack} />}
          <h1 className="text-xl font-semibold text-theme-text-primary flex items-center gap-2">
            <PrinterIcon size={20} />
            {t('manager.modules.printNodePrinters.title', {}, 'PrintNode-skrivere')}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          {lastFetchedAt && (
            <span className="text-xs text-theme-text-secondary">
              {t('manager.modules.printNodePrinters.lastFetched', { '0': lastFetchedAt.toLocaleTimeString('nb-NO') }, `Hentet {0}`)}
            </span>
          )}
          <button
            onClick={load}
            disabled={loading}
            className="flex items-center gap-2 px-3 py-1.5 text-sm rounded-md border border-theme-border-primary text-theme-text-primary hover:bg-theme-bg-secondary disabled:opacity-50"
          >
            <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
            {t('manager.modules.printNodePrinters.refresh', {}, 'Oppdater')}
          </button>
        </div>
      </div>

      <div className="flex gap-1 px-4 pt-3 border-b border-theme-border-primary">
        {(['printers', 'access'] as const).map(id => (
          <button
            key={id}
            onClick={() => setTab(id)}
            className={`px-4 min-h-[44px] text-sm -mb-px border-b-2 ${tab === id ? 'border-blue-600 text-blue-700 font-medium' : 'border-transparent text-theme-text-secondary hover:text-theme-text-primary'}`}
          >
            {id === 'printers'
              ? t('manager.modules.printNodePrinters.tabPrinters', {}, 'Skrivere')
              : t('manager.modules.printNodePrinters.tabAccess', {}, 'Skrivertilgang')}
          </button>
        ))}
      </div>

      {tab === 'access' ? (
        <PrinterAccessPanel companyNo={companyNo} />
      ) : (
      <>
      <div className="p-4 border-b border-theme-border-primary">
        <p className="text-sm text-theme-text-secondary mb-3">
          {t(
            'manager.modules.printNodePrinters.intro',
            {},
            'Rådata fra PrintNode for denne tenantens skrivere. Brukes til å slå opp papirformater, DPI og annet ved feilsøking av rapport-/skriverkonfigurasjon. Ingen redigering her.'
          )}
        </p>

        {account && (
          <div className="mb-3 text-xs text-theme-text-secondary bg-theme-bg-secondary border border-theme-border-primary rounded-md px-3 py-1.5 inline-block">
            {t(
              'manager.modules.printNodePrinters.account',
              { '0': account.numComputers, '1': account.credits, '2': account.totalPrints },
              'Konto: {0} PC-er tilkoblet · {1} kreditter · {2} utskrifter totalt'
            )}
          </div>
        )}
        {accountError && (
          <div className="mb-3 text-xs text-theme-text-secondary italic">
            {t('manager.modules.printNodePrinters.accountLoadFailed', {}, 'Kontoinfo kunne ikke hentes')}
          </div>
        )}

        <div className="relative max-w-sm">
          <Search size={16} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-theme-text-secondary" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('manager.modules.printNodePrinters.searchPlaceholder', {}, 'Søk på skriver, PC eller ID...')}
            className="w-full pl-8 pr-3 py-1.5 text-sm border border-theme-border-primary rounded-md bg-theme-bg-primary text-theme-text-primary"
          />
        </div>
      </div>

      <div className="flex-1 overflow-auto p-4">
        {error && (
          <div className="mb-4 p-3 rounded-md bg-red-50 border border-red-200 text-red-800 text-sm flex items-center gap-2">
            <AlertTriangle size={16} />
            {error}
          </div>
        )}

        {loading && printers.length === 0 && (
          <div className="text-sm text-theme-text-secondary">{t('manager.modules.printNodePrinters.loading', {}, 'Laster skrivere...')}</div>
        )}

        {!loading && !error && printers.length === 0 && (
          <div className="text-sm text-theme-text-secondary">
            {t('manager.modules.printNodePrinters.empty', {}, 'Ingen skrivere funnet. Sjekk at PrintNode-klienten kjører og er koblet til kontoen.')}
          </div>
        )}

        {groups.map(group => (
          <div key={group.computer?.id ?? 'ukjent'} className="mb-6">
            <div className="flex items-center gap-2 mb-2 text-theme-text-primary flex-wrap">
              <Monitor size={16} />
              <h2 className="font-semibold text-sm">
                {group.computer?.hostname || group.computer?.name || t('manager.modules.printNodePrinters.unknownComputer', {}, 'Ukjent PC')}
              </h2>
              {group.computer?.state && (
                <StatusPill online={group.computer.state === 'connected'} label={group.computer.state} />
              )}
              {group.computer?.inet && (
                <span className="text-xs text-theme-text-secondary">{group.computer.inet}</span>
              )}
              {group.computer?.version && (
                <span className="text-xs text-theme-text-secondary">
                  {t('manager.modules.printNodePrinters.clientVersion', { '0': group.computer.version }, 'Klient v{0}')}
                </span>
              )}
              {group.computer?.jre && (
                <span className="text-xs text-theme-text-secondary">JRE {group.computer.jre}</span>
              )}
            </div>
            <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
              {group.printers.map(p => (
                <PrinterCard key={p.id} printer={p} recentJobs={jobsByPrinterId.get(p.id) || []} t={t} />
              ))}
            </div>
          </div>
        ))}
      </div>
      </>
      )}
    </div>
  );
};

type TFn = (key: string, params?: Record<string, string | number | null | undefined>, defaultText?: string) => string;

const PrinterCard: React.FC<{ printer: Printer; recentJobs: PrintJob[]; t: TFn }> = ({ printer, recentJobs, t }) => {
  const caps = useMemo(() => parsePrinterCapabilities(printer.capabilities), [printer.capabilities]);

  return (
    <div className="border border-theme-border-primary rounded-lg p-3 bg-theme-bg-secondary">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <div className="font-semibold text-sm text-theme-text-primary flex items-center gap-1.5">
            {printer.name}
            {printer.default && (
              <span title={t('manager.modules.printNodePrinters.defaultPrinter', {}, 'Standard i Windows')}>
                <Star size={12} className="text-amber-500 fill-amber-500" />
              </span>
            )}
          </div>
          <div className="text-xs text-theme-text-secondary">{printer.description}</div>
        </div>
        <StatusPill online={printer.state === 'online'} label={printer.state} />
      </div>

      <div className="text-xs text-theme-text-secondary mb-2 flex flex-col gap-0.5">
        <div>
          {t('manager.modules.printNodePrinters.printerId', {}, 'PrintNode-ID')}: <span className="font-mono">{printer.id}</span>
        </div>
        {printer.createTimestamp && (
          <div>
            {t('manager.modules.printNodePrinters.registeredAt', { '0': new Date(printer.createTimestamp).toLocaleDateString('nb-NO') }, 'Registrert {0}')}
          </div>
        )}
      </div>

      <div className="flex flex-wrap gap-1.5 mb-2">
        {caps.dpis.map(d => <Chip key={`dpi-${d}`}>{d} DPI</Chip>)}
        {caps.color !== null && (
          <Chip>{caps.color ? t('manager.modules.printNodePrinters.color', {}, 'Farge') : t('manager.modules.printNodePrinters.monochrome', {}, 'Sort/hvitt')}</Chip>
        )}
        {caps.duplex !== null && caps.duplex && <Chip>{t('manager.modules.printNodePrinters.duplex', {}, 'Duplex')}</Chip>}
        {caps.collate !== null && caps.collate && <Chip>{t('manager.modules.printNodePrinters.collate', {}, 'Sortering')}</Chip>}
        {caps.maxCopies !== null && <Chip>{t('manager.modules.printNodePrinters.maxCopies', { '0': caps.maxCopies }, 'Maks {0} kopier')}</Chip>}
        {caps.printRate !== null && <Chip>{t('manager.modules.printNodePrinters.printRate', { '0': caps.printRate }, '{0} sider/min')}</Chip>}
        {caps.nup.length > 0 && (
          <Chip>{t('manager.modules.printNodePrinters.nup', { '0': caps.nup.join(', ') }, 'Sider per ark: {0}')}</Chip>
        )}
        {caps.bins.map(b => <Chip key={`bin-${b}`}>{b}</Chip>)}
        {caps.medias.map(m => <Chip key={`media-${m}`}>{m}</Chip>)}
      </div>

      <div className="text-xs font-medium text-theme-text-secondary mb-1">
        {t('manager.modules.printNodePrinters.paperFormats', {}, 'Papirformater')}
      </div>
      {caps.papers.length === 0 ? (
        <div className="text-xs text-theme-text-secondary italic">
          {t('manager.modules.printNodePrinters.noPaperFormats', {}, 'Ingen rapportert — skriveren støtter ingen faste formater')}
        </div>
      ) : (
        <div className="flex flex-wrap gap-1.5">
          {caps.papers.map(p => (
            <Chip key={p.name} title={p.name}>
              {p.name}: {p.widthMm.toFixed(1)}×{p.heightMm.toFixed(1)} mm
            </Chip>
          ))}
        </div>
      )}

      {caps.extentMm && (
        <div className="text-xs text-theme-text-secondary mt-2">
          {t('manager.modules.printNodePrinters.customRange', {}, 'Egendefinert område')}: {caps.extentMm[0].toFixed(1)}×{caps.extentMm[1].toFixed(1)} – {caps.extentMm[2].toFixed(1)}×{caps.extentMm[3].toFixed(1)} mm
        </div>
      )}

      <div className="text-xs font-medium text-theme-text-secondary mt-3 mb-1">
        {t('manager.modules.printNodePrinters.recentJobsTitle', {}, 'Siste jobber')}
      </div>
      {recentJobs.length === 0 ? (
        <div className="text-xs text-theme-text-secondary italic">
          {t('manager.modules.printNodePrinters.recentJobsEmpty', {}, 'Ingen jobber funnet for denne skriveren')}
        </div>
      ) : (
        <ul className="space-y-1">
          {recentJobs.map(job => (
            <li key={job.id} className="flex items-center justify-between gap-2 text-xs">
              <span className="truncate text-theme-text-primary" title={job.title}>{job.title}</span>
              <span className="flex items-center gap-1.5 shrink-0">
                <span className="text-theme-text-secondary">{new Date(job.createTimestamp).toLocaleString('nb-NO')}</span>
                <JobStateBadge state={job.state} t={t} />
              </span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const JOB_STATE_STYLES: Record<string, string> = {
  done: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-800',
};

const JobStateBadge: React.FC<{ state: string; t: TFn }> = ({ state, t }) => {
  const labels: Record<string, string> = {
    done: t('manager.modules.printNodePrinters.jobState.done', {}, 'Utført'),
    error: t('manager.modules.printNodePrinters.jobState.error', {}, 'Feilet'),
    new: t('manager.modules.printNodePrinters.jobState.new', {}, 'Ny'),
    queued: t('manager.modules.printNodePrinters.jobState.queued', {}, 'I kø'),
    sent: t('manager.modules.printNodePrinters.jobState.sent', {}, 'Sendt'),
  };
  return (
    <span className={`px-1.5 py-0.5 rounded text-[10px] font-medium ${JOB_STATE_STYLES[state] || 'bg-gray-200 text-gray-700'}`}>
      {labels[state] || state}
    </span>
  );
};

const StatusPill: React.FC<{ online: boolean; label: string }> = ({ online, label }) => (
  <span
    className={`inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-medium shrink-0 ${
      online ? 'bg-green-100 text-green-800' : 'bg-gray-200 text-gray-700'
    }`}
    title={label}
  >
    {online ? <CheckCircle2 size={11} /> : <XCircle size={11} />}
    {label}
  </span>
);

const Chip: React.FC<{ children: React.ReactNode; title?: string }> = ({ children, title }) => (
  <span
    title={title}
    className="inline-block px-1.5 py-0.5 rounded text-[10px] bg-theme-bg-primary border border-theme-border-primary text-theme-text-primary"
  >
    {children}
  </span>
);

/**
 * Parser PrintNode sine rå kapabiliteter (tidels mm, snake_case-nøkler for enkelte felt)
 * til lesbare mm-verdier. Rådataene deserialiseres backend-side som `object`, så de
 * ankommer frontend som de rå JSON-typene PrintNode sender (arrays/booleans/objekter).
 * Enhver uventet form ignoreres stille — siden skal aldri krasje på uventet PrintNode-data.
 */
function parsePrinterCapabilities(raw: unknown): ParsedCapabilities {
  const result: ParsedCapabilities = {
    dpis: [],
    bins: [],
    medias: [],
    nup: [],
    papers: [],
    extentMm: null,
    color: null,
    duplex: null,
    collate: null,
    maxCopies: null,
    printRate: null,
    supportsCustomPaperSize: null,
  };
  if (!raw || typeof raw !== 'object') return result;
  const c = raw as Record<string, unknown>;

  if (Array.isArray(c.dpis)) result.dpis = c.dpis.filter((d): d is string => typeof d === 'string');
  if (Array.isArray(c.bins)) result.bins = c.bins.filter((b): b is string => typeof b === 'string');
  if (Array.isArray(c.medias)) result.medias = c.medias.filter((m): m is string => typeof m === 'string');
  if (Array.isArray(c.nup)) result.nup = c.nup.filter((n): n is number => typeof n === 'number');
  if (typeof c.color === 'boolean') result.color = c.color;
  if (typeof c.duplex === 'boolean') result.duplex = c.duplex;
  if (typeof c.collate === 'boolean') result.collate = c.collate;
  if (typeof c.copies === 'number') result.maxCopies = c.copies;
  if (typeof c.printrate === 'number') result.printRate = c.printrate;
  if (typeof c.supports_custom_paper_size === 'boolean') result.supportsCustomPaperSize = c.supports_custom_paper_size;

  if (c.papers && typeof c.papers === 'object' && !Array.isArray(c.papers)) {
    const papers: ParsedPaperFormat[] = [];
    for (const [name, value] of Object.entries(c.papers as Record<string, unknown>)) {
      if (Array.isArray(value) && value.length === 2 && typeof value[0] === 'number' && typeof value[1] === 'number') {
        papers.push({ name, widthMm: value[0] / 10, heightMm: value[1] / 10 });
      }
    }
    papers.sort((a, b) => a.name.localeCompare(b.name));
    result.papers = papers;
  }

  if (
    Array.isArray(c.extent) &&
    c.extent.length === 2 &&
    Array.isArray(c.extent[0]) &&
    Array.isArray(c.extent[1]) &&
    c.extent[0].length === 2 &&
    c.extent[1].length === 2
  ) {
    const [[minW, minH], [maxW, maxH]] = c.extent as [[number, number], [number, number]];
    if ([minW, minH, maxW, maxH].every(v => typeof v === 'number')) {
      result.extentMm = [minW / 10, minH / 10, maxW / 10, maxH / 10];
    }
  }

  return result;
}
